import { Component, Inject, OnInit, ChangeDetectorRef, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Chart } from 'angular-highcharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { RequestFormService } from '../../service/requestForm/request-form.service';

@Component({
  selector: 'app-tp-reference-popup',
  templateUrl: './tp-reference-popup.component.html',
  styleUrls: ['./tp-reference-popup.component.scss']
})
export class TpReferencePopupComponent implements OnInit {
  shopChart: Chart | null = null;
  seaChart: Chart | null = null;
  ladenConsChart: Chart | null = null;
  ballastConsChart: Chart | null = null;

  ladenDrafts: number[] = [];
  ballastDrafts: number[] = [];
  speeds: number[] = [];
  isDownloading = false;

  // Shop curve table toggle
  showShopTable = false;
  shopTableSearch = '';
  shopTableData: { load: number; loadKw: number; sfoc: number; sfocCorr: number; consumption: number }[] = [];

  // Power → Consumption form
  powerInput: number | null = null;
  consumptionFromPower: number | null = null;

  formattedHeavyEq = '';
  formattedLightEq = '';

  // LCV constants (kept at frontend as per spec)
  readonly LCV_ACTUAL = 40500;
  readonly LCV_REF = 42700;

  // Shop trial equations (formatted HTML)
  readonly shopSfocCorrEq = `SFOC<sub>corr</sub> = SFOC<sub>actual</sub> &times; <span class="var-x">LCV<sub>ref</sub></span> / <span class="var-x">LCV<sub>actual</sub></span> &nbsp;&nbsp; (${this.LCV_REF} / ${this.LCV_ACTUAL})`;
  readonly shopConsEq = `Consumption&nbsp;(t/d) = Power&nbsp;(kW) &times; SFOC<sub>corr</sub> &times; 24 / 1&thinsp;000&thinsp;000`;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<TpReferencePopupComponent>,
    private requestFormService: RequestFormService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    this.extractTableData();
    this.initCharts();
    this.formattedHeavyEq = this.formatEquation(this.data.referenceData?.heavyEquation);
    this.formattedLightEq = this.formatEquation(this.data.referenceData?.lightEquation);
    this.buildShopTable();
  }

  /** Interpolate shop curve to get 1-100% rows */
  buildShopTable() {
    const curve: any[] = (this.data.referenceData?.shopCurve || []).slice().sort((a: any, b: any) => a.load - b.load);
    if (!curve.length) return;

    const lerp = (key: string, pct: number): number => {
      // Above last point — clamp
      if (pct >= curve[curve.length - 1].load) return curve[curve.length - 1][key] ?? 0;

      // Below first point — extrapolate
      if (pct < curve[0].load) {
        // Power/kW is proportional to load: scale linearly from 0
        if (key === 'loadKw' || key === 'power') {
          return (pct / curve[0].load) * (curve[0][key] ?? 0);
        }
        // For sfoc, refCons etc: extrapolate using slope of first two points
        if (curve.length >= 2) {
          const x0 = curve[0].load, y0 = curve[0][key] ?? 0;
          const x1 = curve[1].load, y1 = curve[1][key] ?? 0;
          if (x1 !== x0) {
            const slope = (y1 - y0) / (x1 - x0);
            return Math.max(0, y0 + slope * (pct - x0));
          }
        }
        return curve[0][key] ?? 0;
      }

      // Normal interpolation between two bracketing points
      for (let i = 0; i < curve.length - 1; i++) {
        const a = curve[i], b = curve[i + 1];
        if (pct >= a.load && pct <= b.load) {
          const t = (pct - a.load) / (b.load - a.load);
          return (a[key] ?? 0) + t * ((b[key] ?? 0) - (a[key] ?? 0));
        }
      }
      return 0;
    };

    this.shopTableData = Array.from({ length: 100 }, (_, i) => {
      const pct = i + 1;
      const loadKw = +lerp('loadKw', pct).toFixed(1);
      const sfoc = +lerp('sfoc', pct).toFixed(2);
      const sfocCorr = +(sfoc * this.LCV_REF / this.LCV_ACTUAL).toFixed(2);
      const consumption = +(loadKw * sfocCorr * 24 / 1_000_000).toFixed(3);
      return { load: pct, loadKw, sfoc, sfocCorr, consumption };
    });
  }

  get filteredShopTableData() {
    const q = this.shopTableSearch.trim().toLowerCase();
    if (!q) return this.shopTableData;
    return this.shopTableData.filter(r =>
      r.load.toString().includes(q) ||
      r.loadKw.toString().includes(q) ||
      r.sfoc.toString().includes(q) ||
      r.sfocCorr.toString().includes(q) ||
      r.consumption.toString().includes(q)
    );
  }

  onPowerInput(val: string) {
    const p = parseFloat(val);
    if (isNaN(p) || p <= 0) { this.consumptionFromPower = null; return; }
    this.powerInput = p;
    // Interpolate sfocCorr from table
    const row = this.shopTableData.reduce((prev, cur) =>
      Math.abs(cur.loadKw - p) < Math.abs(prev.loadKw - p) ? cur : prev,
      this.shopTableData[0]);
    this.consumptionFromPower = row ? +(p * row.sfocCorr * 24 / 1_000_000).toFixed(3) : null;
  }

  formatEquation(formula: string): string {
    if (!formula || formula === 'N/A') return 'N/A';
    return formula
      .replace(/\^([\d.]+)/g, '<sup>$1</sup>')
      .replace(/x/g, '<span class="var-x">x</span>')
      .replace(/\*/g, '&times;');
  }

  extractTableData() {
    const table = this.data.referenceData?.consumptionTable || [];
    if (table.length > 0) {
      this.speeds = table.map((p: any) => p.speed).sort((a: number, b: number) => a - b);
      const firstLadenRow = table[0]?.laden;
      if (firstLadenRow) this.ladenDrafts = Object.keys(firstLadenRow).map(Number).sort((a, b) => a - b);
      const firstBallastRow = table[0]?.ballast;
      if (firstBallastRow) this.ballastDrafts = Object.keys(firstBallastRow).map(Number).sort((a, b) => a - b);
    }
  }

  initCharts(forceLight = false) {
    const isDark = forceLight ? false : this.requestFormService.isDarkMode();
    const axisColor = isDark ? '#ffffff' : '#424242';
    const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

    this.shopChart = this.createShopChart(axisColor, gridColor);
    this.seaChart = this.createSeaChart(axisColor, gridColor);
    this.ladenConsChart = this.createSpecificConsChart('laden', 'Laden Consumption Curves', '#FF9F0A', axisColor, gridColor);
    this.ballastConsChart = this.createSpecificConsChart('ballast', 'Ballast Consumption Curves', '#30D158', axisColor, gridColor);
  }

  private createShopChart(axisColor: string, gridColor: string) {
    const curve = this.data.referenceData?.shopCurve || [];
    if (curve.length === 0) return null;
    return new Chart({
      chart: { type: 'spline', backgroundColor: 'transparent' },
      title: { text: 'Shop Trial Curve', style: { color: '#4a8ff4', fontSize: '14px', fontWeight: 'bold' } },
      xAxis: { title: { text: 'Load (%)', style: { color: axisColor } }, labels: { style: { color: axisColor }, format: '{value}%' }, lineColor: axisColor },
      yAxis: [
        { title: { text: 'Consumption (t/day)', style: { color: axisColor } }, labels: { style: { color: axisColor } }, gridLineColor: gridColor },
        { title: { text: 'SFOC (g/kWh)', style: { color: axisColor } }, labels: { style: { color: axisColor } }, opposite: true, gridLineColor: 'transparent' }
      ],
      series: [
        { name: 'Ref Consumption', data: curve.map((p: any) => [p.load, p.refCons]), color: '#FF2D55', yAxis: 0 },
        { name: 'SFOC', data: curve.map((p: any) => [p.load, p.sfoc]), color: '#00E5FF', yAxis: 1 }
      ] as any,
      credits: { enabled: false },
      legend: { itemStyle: { color: axisColor } }
    });
  }

  private createSeaChart(axisColor: string, gridColor: string) {
    const curve = this.data.referenceData?.seaCurve || [];
    if (curve.length === 0) return null;
    return new Chart({
      chart: { type: 'spline', backgroundColor: 'transparent' },
      title: { text: 'Sea Trial Curve', style: { color: '#4a8ff4', fontSize: '14px', fontWeight: 'bold' } },
      xAxis: { title: { text: 'Speed (knots)', style: { color: axisColor } }, labels: { style: { color: axisColor } }, lineColor: axisColor },
      yAxis: { title: { text: 'Consumption (t/day)', style: { color: axisColor } }, labels: { style: { color: axisColor } }, gridLineColor: gridColor },
      series: [
        { name: 'Laden', data: curve.map((p: any) => [p.speed, p.consumptionHeavy]), color: '#FF9F0A' },
        { name: 'Ballast', data: curve.map((p: any) => [p.speed, p.consumptionLight]), color: '#30D158' }
      ] as any,
      credits: { enabled: false },
      legend: { itemStyle: { color: axisColor } }
    });
  }

  private createSpecificConsChart(type: 'laden' | 'ballast', title: string, baseColor: string, axisColor: string, gridColor: string) {
    const table = this.data.referenceData?.consumptionTable || [];
    if (table.length === 0) return null;
    const drafts = type === 'laden' ? this.ladenDrafts : this.ballastDrafts;
    const series: any[] = drafts.map(d => ({
      name: `Draft ${d}m`,
      data: table.map((p: any) => [p.speed, p?.[type]?.[d.toString()]]).filter((p: any) => p[1] !== undefined),
      marker: { enabled: false }
    }));
    return new Chart({
      chart: { type: 'spline', backgroundColor: 'transparent' },
      title: { text: title, style: { color: baseColor, fontSize: '14px', fontWeight: 'bold' } },
      xAxis: { title: { text: 'Speed (knots)', style: { color: axisColor } }, labels: { style: { color: axisColor } }, lineColor: axisColor },
      yAxis: { title: { text: 'Consumption (t/day)', style: { color: axisColor } }, labels: { style: { color: axisColor } }, gridLineColor: gridColor },
      series,
      credits: { enabled: false },
      legend: { itemStyle: { fontSize: '10px', color: axisColor } }
    });
  }

  async downloadPDF() {
    this.isDownloading = true;
    this.cdr.detectChanges();

    // Turbo initial delay
    await new Promise(res => setTimeout(res, 300));

    const sections = Array.from(document.querySelectorAll('.capture-section')) as HTMLElement[];
    const footer = document.querySelector('.protected-footer') as HTMLElement;

    if (sections.length === 0) {
      this.isDownloading = false;
      this.cdr.detectChanges();
      return;
    }

    const bgColor = '#ffffff'; // Force light mode always for PDF

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableWidth = pdfWidth - (margin * 2);
      const usableHeight = pdfHeight - (margin * 2);

      const addWatermark = () => {
        const lightLogo = 'assets/images/merakiLogo-vector.png';
        const logoPath = lightLogo;
        pdf.saveGraphicsState();
        pdf.setGState(new (pdf as any).GState({ opacity: 0.35 }));
        const aspectRatio = 573 / 435;
        const logoWidth = 140;
        const logoHeight = logoWidth / aspectRatio;
        pdf.addImage(logoPath, 'PNG', (pdfWidth - logoWidth) / 2, (pdfHeight - logoHeight) / 2, logoWidth, logoHeight);
        pdf.restoreGraphicsState();
      };

      const setPageBg = () => {
        pdf.setFillColor(bgColor);
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
      };

      // 1. INVISIBLE CAPTURE (OPTIMIZATION & NO FLICKER)
      const captureTask = async (el: HTMLElement) => {
        const canvas = await html2canvas(el, {
          scale: 2.0,
          useCORS: true,
          backgroundColor: bgColor,
          logging: false,
          imageTimeout: 10000,
          onclone: (clonedDoc) => {
            // 1. Apply light-mode theme ONLY to the clone via INLINE STYLES (Bulletproof Isolation)
            const wrapper = clonedDoc.querySelector('.dialog-wrapper') as HTMLElement;
            if (wrapper) {
              wrapper.style.background = '#ffffff';
              wrapper.style.color = '#1a202c';
            }

            // Force inline styles for text elements to override any dark mode classes
            clonedDoc.querySelectorAll('.primary-text').forEach((el: any) => el.style.setProperty('color', '#1a202c', 'important'));
            clonedDoc.querySelectorAll('.secondary-text').forEach((el: any) => el.style.setProperty('color', '#64748b', 'important'));
            clonedDoc.querySelectorAll('.accent-text').forEach((el: any) => el.style.setProperty('color', '#1e40af', 'important'));
            clonedDoc.querySelectorAll('.card-style').forEach((el: any) => {
              el.style.setProperty('background', '#f8fafc', 'important');
              el.style.setProperty('border-color', '#e2e8f0', 'important');
            });
            clonedDoc.querySelectorAll('th').forEach((el: any) => {
              el.style.setProperty('background', '#f1f5f9', 'important');
              el.style.setProperty('color', '#64748b', 'important');
            });
            clonedDoc.querySelectorAll('td').forEach((el: any) => el.style.setProperty('border-color', '#edf2f7', 'important'));
            clonedDoc.querySelectorAll('.row-even td').forEach((el: any) => el.style.setProperty('background', '#f8fafc', 'important'));
            clonedDoc.querySelectorAll('.row-odd td').forEach((el: any) => el.style.setProperty('background', '#f1f5f9', 'important'));
            clonedDoc.querySelectorAll('.formula-grid, .view-toggle-btns, .shop-table-search, .calc-input-wrap').forEach((el: any) => el.style.setProperty('background', '#f1f5f9', 'important'));

            // 2. Expand ONLY the clone for full capture
            const content = clonedDoc.querySelector('.content') as HTMLElement;
            if (content) {
              content.style.maxHeight = 'none';
              content.style.height = 'auto';
              content.style.overflow = 'visible';
            }

            // Fix Highcharts SVG colors in the clone (for visibility on white background)
            const allTexts = clonedDoc.querySelectorAll('text');
            allTexts.forEach((t: any) => {
              const fill = t.getAttribute('fill');
              const styleFill = t.style.fill;
              if (fill === '#ffffff' || fill === 'rgb(255, 255, 255)' || styleFill === 'rgb(255, 255, 255)') {
                t.setAttribute('fill', '#424242');
                t.style.fill = '#424242';
              }
            });

            // Fix Grid Lines and Axis lines in the clone
            const allPaths = clonedDoc.querySelectorAll('path');
            allPaths.forEach((p: any) => {
              const stroke = p.getAttribute('stroke');
              if (stroke === 'rgba(255,255,255,0.1)' || stroke === '#ffffff') {
                p.setAttribute('stroke', 'rgba(0,0,0,0.1)');
              }
            });
          }
        });
        return canvas.toDataURL('image/jpeg', 0.95);
      };

      // Start parallel captures
      const sectionImagesPromise = Promise.all(sections.map(s => captureTask(s)));
      const footerImagePromise = footer ? captureTask(footer) : Promise.resolve(null);

      const [sectionImages, footerImage] = await Promise.all([sectionImagesPromise, footerImagePromise]);

      // 2. COMPOSE PDF
      let currentY = margin;
      setPageBg();
      addWatermark();

      // Header
      pdf.setFontSize(18);
      pdf.setTextColor(40, 44, 52);
      pdf.setFont('helvetica', 'bold');
      pdf.text(this.data.vesselName, margin, currentY + 5);

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139);
      const reportDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      pdf.text(`IMO: ${this.data.vesselIMO}  |  Report Date: ${reportDate}`, margin, currentY + 12);
      currentY += 25;

      const addImageToPdf = (imgData: string) => {
        const imgProps = pdf.getImageProperties(imgData);
        let imgWidth = usableWidth;
        let imgHeight = (imgProps.height * imgWidth) / imgProps.width;

        if (imgHeight > usableHeight) {
          imgHeight = usableHeight;
          imgWidth = (imgProps.width * imgHeight) / imgProps.height;
        }

        if (currentY + imgHeight > pdfHeight - margin) {
          pdf.addPage();
          setPageBg();
          addWatermark();
          currentY = margin;
        }

        const xOffset = margin + (usableWidth - imgWidth) / 2;
        pdf.saveGraphicsState();
        pdf.setGState(new (pdf as any).GState({ opacity: 0.8 }));
        pdf.addImage(imgData, 'JPEG', xOffset, currentY, imgWidth, imgHeight);
        pdf.restoreGraphicsState();
        currentY += imgHeight + 8;
      };

      sectionImages.forEach(img => addImageToPdf(img));
      if (footerImage) addImageToPdf(footerImage);

      const fileName = `${this.data.vesselName.replace(/\s+/g, '_')}_TP_Reference.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('PDF Generation failed', error);
    } finally {
      this.isDownloading = false;
      this.cdr.detectChanges();
    }
  }
}
